import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  static automaticRemovalPending = false;
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  static codeRequestPending = false;

  editDiscount = () => {
    this.refs.cartDiscountError.classList.add('hidden');
    const message = this.querySelector('[data-pf-code-error]');
    if (message) message.hidden = true;
  };

  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const input = form.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;
    const code = input.value.trim();
    if (!code) return;
    const previous = this.#existingDiscounts();
    if (previous.length === 1 && previous[0].toLowerCase() === code.toLowerCase()) return;
    // A submitted code replaces the previous code. Ineligible replacements restore it.
    await this.#updateCodes(event, [code], { input, value: input.value, code, previous });
  };

  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!(event.target instanceof Element)) return;
    const pill = event.target.closest('.cart-discount__pill[data-discount-code]');
    const code = pill?.dataset.discountCode;
    if (!code) return;
    await this.#updateCodes(event, this.#existingDiscounts().filter((value) => value !== code));
  };

  async #updateCodes(event, codes, submitted) {
    if (CartDiscount.codeRequestPending || CartDiscount.automaticRemovalPending || !this.dataset.sectionId) return;
    CartDiscount.codeRequestPending = true;
    const sectionId = this.dataset.sectionId;
    const mode = this.closest('theme-drawer') ? 'hydration' : 'full';
    const form = this.querySelector('.cart-discount__form');
    const controls = [...document.querySelectorAll('cart-discount-component button, cart-items-component button, cart-items-component select, cart-items-component input[type="number"]')]
      .filter((control) => 'disabled' in control && !control.disabled);
    controls.forEach((control) => { control.disabled = true; });
    form?.setAttribute('aria-busy', 'true');
    this.editDiscount();
    this.refs.cartDiscountErrorDiscountCode.classList.add('hidden');
    this.refs.cartDiscountErrorShipping.classList.add('hidden');
    const pending = CartDiscountUpdateEvent.createPromise();
    this.dispatchEvent(new CartDiscountUpdateEvent({
      discountCodes: codes.map((code) => ({ code })), promise: pending.promise,
    }));
    const request = async (nextCodes) => {
      const body = { sections: [sectionId], sections_url: window.location.pathname };
      if (nextCodes) body.discount = nextCodes.join(',');
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify(body) }));
      const data = await response.json();
      if (!response.ok || data.errors) throw new Error('Cart discount update failed');
      return data;
    };
    const render = async (data) => {
      const html = data.sections?.[sectionId];
      if (!html) throw new Error('Cart section missing');
      await morphSection(sectionId, html, { mode });
      pending.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
    };
    try {
      let data = await request(codes);
      let invalid = false;
      if (submitted) {
        const applied = data.discount_codes?.find((entry) => entry.code.toLowerCase() === submitted.code.toLowerCase());
        invalid = !applied?.applicable;
        if (invalid) data = await request(submitted.previous);
      }
      await render(data);
      if (invalid) {
        // Keep the attempted value editable; do not erase a newer value typed in flight.
        if (submitted.input.value === submitted.value) this.#handleDiscountError('discount_code');
      } else if (submitted && submitted.input.value === submitted.value) {
        submitted.input.value = '';
      }
    } catch (error) {
      // A failed response may still have changed Shopify. Read its real state once.
      try { await render(await request()); } catch (reconcileError) { pending.reject(reconcileError); }
      const message = this.querySelector('[data-pf-code-error]');
      if (message) message.hidden = false;
      this.dispatchEvent(new CartErrorEvent({ error: 'Cart discount update failed', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      CartDiscount.codeRequestPending = false;
      form?.removeAttribute('aria-busy');
      controls.forEach((control) => { if (control.isConnected) control.disabled = false; });
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  }

  /** Opt out of Kaching's quantity offer using its actual regular-price tier.
   * Changing only private bundle properties preserves products, quantities,
   * personalisation, selling plans and all unrelated discounts.
   */
  removeAutomaticDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!(event.target instanceof Element) || CartDiscount.automaticRemovalPending || CartDiscount.codeRequestPending) return;
    const pill = event.target.closest('[data-pf-discount-adapter="kaching-quantity"]');
    if (!(pill instanceof HTMLElement) || !this.dataset.sectionId) return;
    const title = pill.dataset.automaticDiscount;
    const deal = this.dataset.pfQuantityDeal;
    const regularBar = this.dataset.pfQuantityRegularBar;
    if (!title || !deal || !regularBar) return;
    const allowedProducts = new Set((pill.dataset.discountProductIds || '').split(','));
    const root = window.Shopify?.routes?.root || '/';
    const sectionId = this.dataset.sectionId;
    const mode = this.closest('theme-drawer') ? 'hydration' : 'full';
    const errorElement = this.querySelector('[data-pf-discount-error]');
    if (errorElement instanceof HTMLElement) errorElement.hidden = true;
    CartDiscount.automaticRemovalPending = true;
    const disabled = [...document.querySelectorAll('cart-discount-component button, cart-items-component button, cart-items-component select, cart-items-component input[type="number"]')]
      .filter((control) => 'disabled' in control && !control.disabled);
    disabled.forEach((control) => { control.disabled = true; });
    const pending = CartDiscountUpdateEvent.createPromise();
    this.dispatchEvent(new CartDiscountUpdateEvent({
      discountCodes: this.#existingDiscounts().map((code) => ({ code })),
      promise: pending.promise,
    }));
    let cart;
    let failure;
    const request = async (url, body) => {
      const response = await fetch(url, body === undefined ? { headers: { Accept: 'application/json' } } : fetchConfig('json', { body: JSON.stringify(body) }));
      const data = await response.json();
      if (!response.ok || data.errors) throw new Error('Cart discount update failed');
      return data;
    };
    const matches = (allocation) => allocation.discount_application?.type !== 'discount_code'
      && allocation.discount_application?.title === title;
    const identity = (line) => JSON.stringify([
      line.variant_id,
      line.selling_plan_allocation?.selling_plan?.id || null,
      Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
    ]);
    try {
      cart = await request(root + 'cart.js');
      const targets = cart.items.filter((line) => line.line_level_discount_allocations?.some(matches));
      // Validate every affected row before making the first write. A new unrelated
      // automatic offer with a similar title must never enter this adapter.
      for (const line of targets) {
        if (!allowedProducts.has(String(line.product_id))) throw new Error('Unknown quantity discount');
        const allocations = line.line_level_discount_allocations.filter(matches);
        if (allocations.some((entry) => Number(entry.discount_application.value) !== 5 || entry.discount_application.value_type !== 'percentage')) throw new Error('Quantity offer changed');
        const bundle = line.properties?.__kaching_bundles ? JSON.parse(line.properties.__kaching_bundles) : {};
        if ((bundle.deal && bundle.deal !== deal) || bundle.gift || bundle.upsell || bundle.checkboxUpsell || bundle.bundleProduct || bundle.bxgy) throw new Error('Unsupported bundle type');
      }
      for (const target of targets) {
        const line = cart.items.find((candidate) => identity(candidate) === identity(target));
        if (!line || line.quantity !== target.quantity) throw new Error('Cart changed during discount removal');
        const bundle = line.properties?.__kaching_bundles ? JSON.parse(line.properties.__kaching_bundles) : {};
        delete bundle.bid;
        const properties = {
          ...line.properties,
          __kaching_bundles: JSON.stringify({ ...bundle, deal, pp: line.product_id, main: true, bar: regularBar, id: 'pf-regular-' + line.product_id }),
        };
        cart = await request(root + 'cart/change.js', { id: line.key, quantity: line.quantity, properties });
      }
      if (cart.items.some((line) => line.line_level_discount_allocations?.some(matches))) throw new Error('Quantity discount still applied');
    } catch (error) {
      failure = error;
    }
    // Always reconcile actual server state, also after a partial or failed update.
    try {
      cart = await request(Theme.routes.cart_update_url, { sections: [sectionId] });
      pending.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart) });
      if (cart.sections?.[sectionId]) morphSection(sectionId, cart.sections[sectionId], { mode });
      if (failure) {
        for (const message of document.querySelectorAll('pf-cart-tools [data-pf-discount-error]')) message.hidden = false;
      }
    } catch (error) {
      pending.reject(error);
      if (errorElement instanceof HTMLElement) errorElement.hidden = false;
      this.dispatchEvent(new CartErrorEvent({ error: 'Cart discount update failed', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      CartDiscount.automaticRemovalPending = false;
      disabled.forEach((control) => { if (control.isConnected) control.disabled = false; });
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill[data-discount-code]');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
